import { useLanguage } from "../context/LanguageContext";
import PageHero from "../components/PageHero";
import { Reveal, StaggerGroup, StaggerItem } from "../components/Reveal";
import { Users, Handshake, Award, Globe2 } from "lucide-react";
import { motion } from "framer-motion";

const TRANSLATIONS = {
  en: {
    heroTitlePrefix: "Strong ",
    heroTitleHighlight: "alliances",
    heroTitleSuffix: ".",

    heroEyebrow: "Our partners",
    heroTitle: "Strong alliances.",
    heroSubtitle: "We collaborate with the best technology providers and industry leaders to deliver exceptional energy solutions.",

    introEyebrow: "Together we grow",
    introTitle: "Strategic partnerships that power the energy transition.",
    introDesc: "WSBP works hand-in-hand with global technology companies, utilities, and research institutions. These partnerships allow us to integrate cutting-edge solutions and deliver projects with the highest standards of quality and innovation.",

    partnersEyebrow: "Trusted by",
    partnersTitle: "Official partners",

    whyEyebrow: "Why partner with WSBP",
    whyTitle: "Mutual growth through expertise.",
    whyList: [
      { icon: Handshake, title: "Technical Excellence", desc: "Deep expertise in PPC, SCADA, BESS and substation automation." },
      { icon: Award, title: "Proven Track Record", desc: "Over 1 GW of integrated renewable projects across Europe." },
      { icon: Globe2, title: "International Reach", desc: "Active in Portugal, Romania and expanding across European markets." },
      { icon: Users, title: "Long-term Collaboration", desc: "We build lasting relationships based on trust and shared goals." }
    ],

    ctaTitle: "Join our network",
    ctaDesc: "Interested in becoming a WSBP partner? Let's discuss how we can grow together.",
    ctaButton: "Contact us"
  },
  pt: {
    heroTitlePrefix: "Alianças ",
    heroTitleHighlight: "fortes", 
    heroTitleSuffix: ".",

    heroEyebrow: "Os Nossos Parceiros",
    heroTitle: "Alianças fortes.",
    heroSubtitle: "Colaboramos com os melhores fornecedores de tecnologia e líderes da indústria para entregar soluções energéticas excecionais.",

    introEyebrow: "Crescemos juntos",
    introTitle: "Parcerias estratégicas que impulsionam a transição energética.",
    introDesc: "A WSBP trabalha em estreita colaboração com empresas globais de tecnologia, utilities e instituições de investigação. Estas parcerias permitem-nos integrar soluções de ponta e entregar projetos com os mais altos padrões de qualidade e inovação.",

    partnersEyebrow: "Confiança mútua",
    partnersTitle: "Parceiros oficiais",

    whyEyebrow: "Porquê ser nosso parceiro",
    whyTitle: "Crescimento mútuo através da excelência.",
    whyList: [
      { icon: Handshake, title: "Excelência Técnica", desc: "Conhecimento em PPC, SCADA, BESS e automação de subestações." },
      { icon: Award, title: "Histórico Comprovado", desc: "Mais de 1 GW de projetos renováveis integrados em toda a Europa." },
      { icon: Globe2, title: "Presença Internacional", desc: "Ativos em Portugal, Roménia e em expansão pelos principais mercados europeus." },
      { icon: Users, title: "Colaboração de Longo Prazo", desc: "Construímos relações duradouras baseadas em confiança e objetivos comuns." }
    ],

    ctaTitle: "Junte-se à nossa rede",
    ctaDesc: "Interessado em ser um corpo parceiro WSBP? Vamos discutir como podemos crescer juntos.",
    ctaButton: "Contacte-nos"
  }
};

// 1. ADICIONADOS OS LINKS DE CADA PARCEIRO AQUI (Substitua pelos URLs reais se necessário)
const PARTNERS_LOGOS = [
  { name: "COPADATA", logo: "/Partners_logos/COPADATA_logo.png", url: "https://www.copadata.com" },
  { name: "Seneca", logo: "/Partners_logos/SENECA_logo.png", url: "https://www.seneca.it" },
  { name: "DOMAT", logo: "/Partners_logos/DOMAT_logo.png", url: "https://www.domat-int.com/en" }
];

export default function Partners() {
  const { language } = useLanguage();
  const t = TRANSLATIONS[language];

  return (
    <div data-testid="partners-page">
      <PageHero
          eyebrow={<div className="min-w-[120px]">{t.heroEyebrow}</div>}
          title={
          <>
            {language === "pt" ? (
              <><span className="text-[#F68A31]">Alianças</span> fortes.</>
            ) : (
              <>Strong <span className="text-[#F68A31]">alliances</span>.</>
            )}
          </>
        }
        subtitle={t.heroSubtitle}
        image="https://images.pexels.com/photos/33706880/pexels-photo-33706880.jpeg"
      />

      {/* INTRO SECTION */}
      <section className="relative z-10 mx-auto max-w-[1400px] px-6 py-16 md:px-12 lg:px-16">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="eyebrow">{t.introEyebrow}</div>
              <h2 className="font-display mt-6 text-4xl font-bold tracking-tight sm:text-5xl">
                {t.introTitle}
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7 lg:pt-2">
            <Reveal delay={0.1}>
              <p className="text-lg text-white/75 leading-relaxed">
                {t.introDesc}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PARTNERS GRID */}
      <section className="relative z-10 py-20 bg-black/40">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <Reveal>
            <div className="mb-16">
              <div className="eyebrow">{t.partnersEyebrow}</div>
              <h2 className="font-display mt-6 text-4xl font-bold">{t.partnersTitle}</h2>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {PARTNERS_LOGOS.map((partner, idx) => (
                <a 
                  key={idx} 
                  href={partner.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex h-32 sm:h-40 lg:h-48 items-center justify-center rounded-2xl sm:rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8 transition-all hover:border-[#F68A31]/50 hover:bg-white/[0.06] cursor-pointer"
                >
                  <img
                    src={partner.logo}
                    alt={partner.name}
                    className="max-h-12 sm:max-h-16 lg:max-h-20 w-full object-contain opacity-90 transition-all duration-300 group-hover:scale-105"
                    loading="lazy"
                    decoding="async"
                  />
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY PARTNER WITH US */}
      <section className="relative z-10 -mt-16 py-20 bg-black/40">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <Reveal>
            <div className="eyebrow">{t.whyEyebrow}</div>
            <h2 className="font-display mt-6 max-w-4xl text-4xl font-bold tracking-tight sm:text-5xl">
              {t.whyTitle}
            </h2>
          </Reveal>

          <StaggerGroup className="mt-14 grid gap-4 sm:gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
            {t.whyList.map((item, idx) => {
              const IconComponent = item.icon;
              return (
                <StaggerItem key={idx}>
                  <div className="h-full rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8 transition-colors hover:border-[#F68A31]/50">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#F68A31]/10 text-[#F68A31]">
                      <IconComponent size={28} />
                    </div>
                    <h3 className="font-display mt-8 text-2xl font-semibold">
                      {item.title}
                    </h3>
                    <p className="mt-4 text-white/70">{item.desc}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative z-10 mx-auto max-w-[1400px] px-6 py-24 text-center border-t border-white/10">
        <Reveal>
          <h2 className="text-4xl md:text-5xl font-bold font-display">{t.ctaTitle}</h2>
          <p className="mt-6 text-lg text-white/70 max-w-xl mx-auto">{t.ctaDesc}</p>
          <a href="/contact" className="btn-primary mt-10 inline-flex items-center gap-3 text-lg px-12 py-4">
            {t.ctaButton} <Users size={22} />
          </a>
        </Reveal>
      </section>
    </div>
  );
}